"use client"

import AssetComboIcon from "@src/components/DefuseSDK/components/Asset/AssetComboIcon"
import { invalidatePrivateBalanceQueries } from "@src/components/DefuseSDK/features/account/queries/privateBalanceQueries"
import { fetchConfidentialExecutionStatus } from "@src/components/DefuseSDK/features/machines/privateIntents"
import { invalidateBalanceQueries } from "@src/components/DefuseSDK/queries/balanceQueries"
import type { BaseTokenInfo } from "@src/components/DefuseSDK/types/base"
import { formatTokenValue } from "@src/components/DefuseSDK/utils/format"
import { ProgressSteps } from "@src/components/ProgressIndicator"
import { useConnectWallet } from "@src/hooks/useConnectWallet"
import {
  type ReactNode,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react"
import { useActivityDock } from "./ActivityDockProvider"

export const CONFIDENTIAL_STAGES = ["processing", "complete"] as const
export const CONFIDENTIAL_STAGE_LABELS = {
  processing: "Processing",
  complete: "Complete",
}

type Mode = "shield" | "unshield" | "transfer"

type RegisterConfidentialTransferParams = {
  mode: Mode
  depositAddress: string
  token: BaseTokenInfo
  amount: bigint
  recipient?: string
  recipientContactName?: string
}

export type TrackedConfidentialTransfer = {
  id: string
  mode: Mode
  depositAddress: string
  token: BaseTokenInfo
  amount: bigint
  recipient: string | null
  recipientContactName: string | null
  status: "processing" | "success" | "failed"
  createdAt: Date
  completedAt: Date | null
  seenOnPage: boolean
}

type ConfidentialTransferTrackerContextType = {
  trackedTransfers: TrackedConfidentialTransfer[]
  registerConfidentialTransfer: (
    params: RegisterConfidentialTransferParams
  ) => void
  markConfidentialTransferSeen: (id: string) => void
}

const ConfidentialTransferTrackerContext = createContext<
  ConfidentialTransferTrackerContextType | undefined
>(undefined)

export function ConfidentialTransferTrackerProvider({
  children,
}: { children: ReactNode }) {
  const { state } = useConnectWallet()
  const { addDockItem, hasDockItem, settleDockItem, updateDockItem } =
    useActivityDock()
  const [trackedTransfers, setTrackedTransfers] = useState<
    TrackedConfidentialTransfer[]
  >([])
  const setTrackedTransfersRef = useRef(setTrackedTransfers)
  setTrackedTransfersRef.current = setTrackedTransfers

  const prevAddressRef = useRef(state.address)
  useEffect(() => {
    // Clear tracked transfers when wallet address changes
    if (prevAddressRef.current !== state.address) {
      prevAddressRef.current = state.address
      setTrackedTransfers([])
    }
  }, [state.address])

  const registerConfidentialTransfer = useCallback(
    ({
      mode,
      depositAddress,
      token,
      amount,
      recipient,
      recipientContactName,
    }: RegisterConfidentialTransferParams) => {
      const id = `confidential-${depositAddress}`
      if (hasDockItem(id)) return

      setTrackedTransfers((prev) => [
        {
          id,
          mode,
          depositAddress,
          token,
          amount,
          recipient: recipient ?? null,
          recipientContactName: recipientContactName ?? null,
          status: "processing",
          createdAt: new Date(),
          completedAt: null,
          seenOnPage: false,
        },
        ...prev,
      ])

      const modeLabel =
        mode === "shield"
          ? "Move to Confidential"
          : mode === "unshield"
            ? "Move to Main"
            : "Confidential send"
      const formattedAmount = formatTokenValue(amount, token.decimals, {
        min: 0.0001,
        fractionDigits: 4,
      })

      addDockItem({
        id,
        title: `${modeLabel} ${formattedAmount} ${token.symbol}`,
        icons: [
          <AssetComboIcon
            key="token"
            sizeClassName="size-7"
            icon={token.icon}
          />,
        ],
        keyValueRows: [],
        renderContent: () => (
          <ProgressSteps
            stages={CONFIDENTIAL_STAGES}
            stageLabels={CONFIDENTIAL_STAGE_LABELS}
            displayStage="processing"
            displayIndex={0}
            isError={false}
            isSuccess={false}
            size="sm"
          />
        ),
      })

      void trackConfidentialTransfer({
        depositAddress,
        dockId: id,
        settleDockItem,
        updateDockItem,
        onStatusChange: (status: "success" | "failed") => {
          setTrackedTransfersRef.current((prev) =>
            prev.map((t) =>
              t.id === id ? { ...t, status, completedAt: new Date() } : t
            )
          )
        },
      })
    },
    [addDockItem, hasDockItem, settleDockItem, updateDockItem]
  )

  const markConfidentialTransferSeen = useCallback((id: string) => {
    setTrackedTransfers((prev) =>
      prev.map((t) => (t.id === id ? { ...t, seenOnPage: true } : t))
    )
  }, [])

  const value = useMemo(
    () => ({
      trackedTransfers,
      registerConfidentialTransfer,
      markConfidentialTransferSeen,
    }),
    [
      trackedTransfers,
      registerConfidentialTransfer,
      markConfidentialTransferSeen,
    ]
  )

  return (
    <ConfidentialTransferTrackerContext.Provider value={value}>
      {children}
    </ConfidentialTransferTrackerContext.Provider>
  )
}

export function useConfidentialTransferTracker() {
  const context = useContext(ConfidentialTransferTrackerContext)

  if (!context) {
    throw new Error(
      "useConfidentialTransferTracker must be used within a ConfidentialTransferTrackerProvider"
    )
  }

  return context
}

async function trackConfidentialTransfer({
  depositAddress,
  dockId,
  settleDockItem,
  updateDockItem,
  onStatusChange,
}: {
  depositAddress: string
  dockId: string
  settleDockItem: (id: string) => void
  updateDockItem: (
    id: string,
    updates: {
      renderContent?: () => ReactNode
    }
  ) => void
  onStatusChange: (status: "success" | "failed") => void
}) {
  const maxAttempts = 120
  let lastStatus = "PROCESSING"

  for (let attempt = 0; attempt < maxAttempts; attempt += 1) {
    const statusResult = await fetchConfidentialExecutionStatus(depositAddress)

    if ("ok" in statusResult && statusResult.ok) {
      lastStatus = statusResult.ok.status.toUpperCase()

      if (lastStatus === "SUCCESS") {
        onStatusChange("success")
        updateDockItem(dockId, {
          renderContent: () => (
            <ProgressSteps
              stages={CONFIDENTIAL_STAGES}
              stageLabels={CONFIDENTIAL_STAGE_LABELS}
              displayStage="complete"
              displayIndex={1}
              isError={false}
              isSuccess={true}
              size="sm"
            />
          ),
        })
        settleDockItem(dockId)
        invalidatePrivateBalanceQueries()
        invalidateBalanceQueries()
        return
      }

      if (lastStatus === "FAILED" || lastStatus === "REFUNDED") {
        break
      }
    }

    await new Promise((resolve) => setTimeout(resolve, 1000))
  }

  const isFailed = lastStatus === "FAILED" || lastStatus === "REFUNDED"

  onStatusChange("failed")

  updateDockItem(dockId, {
    renderContent: () => (
      <ProgressSteps
        stages={CONFIDENTIAL_STAGES}
        stageLabels={CONFIDENTIAL_STAGE_LABELS}
        displayStage={isFailed ? "complete" : "processing"}
        displayIndex={isFailed ? 1 : 0}
        isError={true}
        isSuccess={false}
        size="sm"
      />
    ),
  })

  settleDockItem(dockId)
}
